import { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, Search } from 'lucide-react';
import { logger } from '../../utils/logger';

const PlayerSelect = ({ name, label, players, onAddNew, exclude = [], value, onChange }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedValue, setSelectedValue] = useState(value || '');
  const [justAddedPlayerId, setJustAddedPlayerId] = useState(null);
  const [justAddedPlayerName, setJustAddedPlayerName] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Sync with external value prop
  useEffect(() => {
    if (justAddedPlayerId) {
      const playerExists = players.some(p => p.id === justAddedPlayerId);
      if (playerExists) {
        if (value === justAddedPlayerId) {
          setSelectedValue(justAddedPlayerId);
          setJustAddedPlayerId(null);
          setJustAddedPlayerName('');
        } else {
          setSelectedValue(justAddedPlayerId);
        }
        return;
      }
    }

    if (value !== undefined && value !== selectedValue && !justAddedPlayerId) {
      setSelectedValue(value || '');
    }
  }, [value, selectedValue, justAddedPlayerId, players]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const [newValue, setNewValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAdd = async () => {
    if (!newValue.trim()) return;
    setLoading(true);
    setError('');
    const nameToAdd = newValue.trim();
    try {
      const newId = await onAddNew(newValue);
      logger.debug('PlayerSelect: newId received:', newId);
      if (newId) {
        setJustAddedPlayerId(newId);
        setJustAddedPlayerName(nameToAdd);
        setSelectedValue(newId);
        onChange && onChange(newId);
        setIsAdding(false);
        setNewValue('');
      } else {
        logger.warn('PlayerSelect: No newId returned from onAddNew');
        setIsAdding(false);
        setNewValue('');
      }
    } catch (e) {
      let errorMessage = e.message;
      if (errorMessage.includes('Bad Request:')) {
        try {
          const jsonMatch = errorMessage.match(/\{.*\}/);
          if (jsonMatch) {
            const errorObj = JSON.parse(jsonMatch[0]);
            errorMessage = errorObj.error || 'Failed to create player';
          }
        } catch {
          errorMessage = 'Failed to create player. Please try again.';
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleSelect = (playerId) => {
    if (playerId === 'NEW_PLAYER') {
      setIsAdding(true);
      setIsOpen(false);
      setSearchQuery('');
    } else {
      setSelectedValue(playerId);
      onChange && onChange(playerId);
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  const getSelectedPlayerName = () => {
    if (justAddedPlayerId) {
      return `${justAddedPlayerName} (New)`;
    }
    const player = players.find(p => p.id === selectedValue);
    return player?.name || '';
  };

  // Filter players based on search query
  const filteredPlayers = [...players]
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter(p =>
      p.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

  return (
    <div className="w-full group">
      {/* Hidden input for form submission */}
      <input type="hidden" name={name} value={selectedValue} />

      {isAdding ? (
        <>
          <div className="flex gap-2 animate-in fade-in duration-200">
            <input
              type="text"
              autoFocus
              className="w-full bg-zinc-50 border border-zinc-300 rounded px-3 py-2 text-sm text-zinc-900 focus:border-zinc-900 outline-none placeholder:text-zinc-400"
              placeholder="Enter Name..."
              value={newValue}
              onChange={e => setNewValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={loading}
              className="px-3 py-2 bg-zinc-900 text-white rounded text-xs font-bold hover:bg-zinc-800 disabled:opacity-50"
            >
              {loading ? '...' : 'Add'}
            </button>
            <button
              type="button"
              onClick={() => {
                setIsAdding(false);
                setError('');
                setNewValue('');
              }}
              className="px-2 text-zinc-400 hover:text-zinc-900"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {error && (
            <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded px-2 py-1">
              {error}
            </div>
          )}
        </>
      ) : (
        <div className="relative" ref={dropdownRef}>
          {/* Combobox trigger */}
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`w-full bg-white border rounded px-3 py-2.5 text-sm text-left font-medium outline-none cursor-pointer transition-colors shadow-sm flex items-center justify-between ${
              isOpen
                ? 'border-zinc-900 ring-1 ring-zinc-900'
                : 'border-zinc-200 hover:border-zinc-300'
            }`}
          >
            <span className={selectedValue ? 'text-zinc-900' : 'text-zinc-400'}>
              {selectedValue ? getSelectedPlayerName() : 'Select Player...'}
            </span>
            <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown */}
          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
              {/* Search input */}
              <div className="p-2 border-b border-zinc-100">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-zinc-400" />
                  <input
                    ref={inputRef}
                    type="text"
                    className="w-full bg-zinc-50 border border-zinc-200 rounded pl-8 pr-3 py-2 text-sm outline-none focus:border-zinc-400 placeholder:text-zinc-400"
                    placeholder="Type to search..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Escape') {
                        setIsOpen(false);
                        setSearchQuery('');
                      }
                      if (e.key === 'Enter' && filteredPlayers.length === 1) {
                        e.preventDefault();
                        handleSelect(filteredPlayers[0].id);
                      }
                    }}
                  />
                </div>
              </div>

              {/* Options list */}
              <div className="max-h-48 overflow-y-auto">
                {/* Just added player */}
                {justAddedPlayerId && (
                  <button
                    type="button"
                    onClick={() => handleSelect(justAddedPlayerId)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-50 flex items-center gap-2 ${
                      selectedValue === justAddedPlayerId ? 'bg-zinc-100 font-medium' : ''
                    }`}
                  >
                    {justAddedPlayerName}
                    <span className="text-xs text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">New</span>
                  </button>
                )}

                {filteredPlayers.length === 0 && !justAddedPlayerId ? (
                  <div className="px-3 py-4 text-sm text-zinc-400 text-center">
                    No players found
                  </div>
                ) : (
                  filteredPlayers.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => !exclude.includes(p.id) && handleSelect(p.id)}
                      disabled={exclude.includes(p.id)}
                      className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                        exclude.includes(p.id)
                          ? 'text-zinc-300 cursor-not-allowed bg-zinc-50'
                          : selectedValue === p.id
                            ? 'bg-zinc-100 font-medium text-zinc-900'
                            : 'hover:bg-zinc-50 text-zinc-700'
                      }`}
                    >
                      {p.name}
                      {exclude.includes(p.id) && (
                        <span className="ml-2 text-xs text-zinc-400">(Selected)</span>
                      )}
                    </button>
                  ))
                )}

                {/* Separator and Add New */}
                <div className="border-t border-zinc-100 mt-1">
                  <button
                    type="button"
                    onClick={() => handleSelect('NEW_PLAYER')}
                    className="w-full px-3 py-2.5 text-left text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
                  >
                    + Add New Player
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {label && (
        <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-zinc-400 pl-1">
          {label}
        </div>
      )}
    </div>
  );
};

export default PlayerSelect;
